import React, { memo, PropsWithChildren, useEffect, useMemo, useState } from "react";

import {
    AgeGroup,
    DecadeByAgeGroup,
    SeniorsAgeGroups,
    SeniorsDecadeByAgeGroup,
} from "@/components/ChooseAgeGroup/utils";
import { UnitTypes } from "@/constants/bodyMetrics";
import { CountryCodes } from "@/constants/currency";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import useFunnelFoxAnswers from "@/hooks/useFunnelFoxAnswers";
import { Train3MonthsAnswers } from "@/types/answers";
import { useGeolocation } from "@/utils/GeolocationProvider";
import { email as paramEmail } from "@/utils/settings";
import { useGrowthBook } from "@growthbook/growthbook-react";

import { getIsFunnelApplovin } from "./applovinFunnelUtils";
import { localStorageManager, LocalStorageManagerKeys } from "./localStorageManager";

export const TARGET_WEIGHT_STRING_KEY = "frontTargetWeightString";
export const CURRENT_WEIGHT_STRING_KEY = "frontCurrentWeightString";

export type AnswerValueType =
    | boolean
    | string
    | number
    | string[]
    | number[]
    | null
    | Date
    | object[];
export type Answers = Record<string, AnswerValueType>;

export interface AnswersProviderProps {
    answers: Answers;
    setAnswers: React.Dispatch<React.SetStateAction<Answers>>;
    name: string;
    setName: React.Dispatch<React.SetStateAction<string>>;
    email: string;
    setEmail: React.Dispatch<React.SetStateAction<string>>;
    isSkipDateChart: boolean;
    phone?: string;
    setPhone: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const defaultState: AnswersProviderProps = {
    answers: {},
    setAnswers: () => null,
    name: "Champ",
    setName: () => null,
    email: "",
    setEmail: () => null,
    isSkipDateChart: false,
    setPhone: () => null,
};

export const AnswersContext = React.createContext<AnswersProviderProps>(defaultState);

const getDecadeFromSavedAgeGroup = (ageGroup: string | undefined): string | undefined => {
    if (!ageGroup) return undefined;
    if (ageGroup in DecadeByAgeGroup) {
        return DecadeByAgeGroup[ageGroup as AgeGroup];
    }
    if (ageGroup in SeniorsDecadeByAgeGroup) {
        return SeniorsDecadeByAgeGroup[ageGroup as SeniorsAgeGroups];
    }
    return undefined;
};

const AnswersProvider = ({ children }: PropsWithChildren) => {
    const savedAgeGroup = localStorageManager.getStoredItem(LocalStorageManagerKeys.AGE_GROUP) as
        | AgeGroup
        | undefined;
    const savedAgeGroupDecade = localStorageManager.getStoredItem<string>(
        LocalStorageManagerKeys.AGE_GROUP_DECADE
    );
    const initialAgeGroupDecade =
        savedAgeGroupDecade ?? getDecadeFromSavedAgeGroup(savedAgeGroup as string | undefined);
    const defaultStateAnswers: Answers = {
        ...defaultState.answers,
        ...(savedAgeGroup && { [QUESTIONS_IDS.AGE_GROUP]: savedAgeGroup }),
        ...(initialAgeGroupDecade && { [QUESTIONS_IDS.AGE_GROUP_DECADE]: initialAgeGroupDecade }),
    };
    const [answers, setAnswers] = useState(defaultStateAnswers);
    const savedName = localStorageManager.getStoredItem(LocalStorageManagerKeys.NAME) as
        | string
        | undefined;
    const [name, setName] = useState(savedName ?? defaultState.name);
    const savedEmail = localStorageManager.getStoredItem(LocalStorageManagerKeys.EMAIL) as
        | string
        | undefined;
    const [email, setEmail] = useState(savedEmail ?? defaultState.email);
    const [phone, setPhone] = useState<string>();
    const isFunnelApplovin = useMemo(() => getIsFunnelApplovin(), []);
    const ageGroup = answers[QUESTIONS_IDS.AGE_GROUP] as AgeGroup;
    const ageGroupDecade = answers[QUESTIONS_IDS.AGE_GROUP_DECADE] as string | undefined;

    const isSkipDateChart = useMemo(
        () =>
            answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] === "none" ||
            answers?.[QUESTIONS_IDS.EVENT_DATE] === "skip" ||
            !answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] ||
            isFunnelApplovin,
        [answers, isFunnelApplovin]
    );

    const { country } = useGeolocation();

    const { isFunnelFoxLink } = useFunnelFoxAnswers({ setEmail, setAnswers, setName });

    useEffect(() => {
        email && localStorageManager.storeItem(LocalStorageManagerKeys.EMAIL, email);
    }, [email]);

    useEffect(() => {
        name && localStorageManager.storeItem(LocalStorageManagerKeys.NAME, name);
    }, [name]);

    useEffect(() => {
        ageGroup && localStorageManager.storeItem(LocalStorageManagerKeys.AGE_GROUP, ageGroup);
    }, [ageGroup]);

    useEffect(() => {
        ageGroupDecade &&
            localStorageManager.storeItem(LocalStorageManagerKeys.AGE_GROUP_DECADE, ageGroupDecade);
    }, [ageGroupDecade]);

    const growthBook = useGrowthBook();
    const train3MonthsAnswer = answers[QUESTIONS_IDS.TRAIN_3_MONTHS] as Train3MonthsAnswers;
    useEffect(() => {
        if (train3MonthsAnswer) {
            const isBeginnerUser = train3MonthsAnswer === Train3MonthsAnswers.HardlyEver;
            growthBook?.updateAttributes({ isBeginnerUser });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [train3MonthsAnswer]);

    useEffect(() => {
        if (ageGroup) {
            growthBook?.updateAttributes({ ageGroup });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ageGroup]);

    useEffect(() => {
        if (paramEmail && !isFunnelFoxLink) {
            setEmail(paramEmail);
        }
    }, [paramEmail, isFunnelFoxLink]);

    useEffect(() => {
        setAnswers((prev) => ({
            ...prev,
            units: country === CountryCodes.US ? UnitTypes.Imperial : UnitTypes.Metric,
        }));
    }, [country]);

    return (
        <AnswersContext.Provider
            value={{
                answers,
                setAnswers,
                name,
                setName,
                email,
                setEmail,
                isSkipDateChart,
                phone,
                setPhone,
            }}
        >
            {children}
        </AnswersContext.Provider>
    );
};

export default memo(AnswersProvider);
